import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Mail, Award, Users, Rocket } from 'lucide-react';
import './Team.css';

const Team = () => {
  const teamMembers = [
    {
      name: "Ahmed Qaddoura",
      role: "Founder & CEO",
      bio: "Visionary entrepreneur with 8+ years in fitness technology. Former fitness coach turned tech innovator.",
      expertise: ["Product Strategy", "Business Development", "Fitness Industry"],
      image: "/profile.jpg",
      linkedin: "#",
      twitter: "#",
      email: "ahmed@fithive.com"
    },
    {
      name: "Sarah Chen",
      role: "CTO & Co-Founder",
      bio: "Tech leader with 12+ years building scalable platforms. Former senior engineer at major tech companies.",
      expertise: ["AI/ML", "Platform Architecture", "Team Leadership"],
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      linkedin: "#",
      twitter: "#",
      email: "sarah@fithive.com"
    },
    {
      name: "Marcus Rodriguez",
      role: "Head of Product",
      bio: "Product strategist with deep understanding of user experience and fitness industry trends.",
      expertise: ["Product Design", "User Research", "Fitness Technology"],
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      linkedin: "#",
      twitter: "#",
      email: "marcus@fithive.com"
    }
  ];

  const advisors = [
    {
      name: "Dr. Emily Watson",
      role: "Fitness Industry Advisor",
      bio: "Former VP at major fitness chains with 20+ years industry experience.",
      company: "Fitness Industry Expert"
    },
    {
      name: "James Kim",
      role: "Technology Advisor",
      bio: "Serial entrepreneur and investor in health tech startups.",
      company: "HealthTech Ventures"
    }
  ];

  return (
    <section className="team" id="team">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
                  <h2 className="section-title">Meet Our Team</h2>
        <p className="section-subtitle">
          The passionate minds behind AllFitHive's mission
        </p>
        </motion.div>

        <motion.div
          className="team-grid"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              className="team-member"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <div className="member-image">
                <img src={member.image} alt={member.name} />
                <div className="member-overlay">
                  <div className="social-links">
                    <a href={member.linkedin} className="social-link">
                      <Linkedin size={20} />
                    </a>
                    <a href={member.twitter} className="social-link">
                      <Twitter size={20} />
                    </a>
                    <a href={`mailto:${member.email}`} className="social-link">
                      <Mail size={20} />
                    </a>
                  </div>
                </div>
              </div>
              
              <div className="member-info">
                <h3 className="member-name">{member.name}</h3>
                <p className="member-role">{member.role}</p>
                <p className="member-bio">{member.bio}</p>
                
                <div className="member-expertise">
                  {member.expertise.map((skill, skillIndex) => (
                    <span key={skillIndex} className="expertise-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="advisors-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <h3>Advisory Board</h3>
          <div className="advisors-grid">
            {advisors.map((advisor, index) => (
              <motion.div
                key={index}
                className="advisor-card"
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="advisor-info">
                  <h4>{advisor.name}</h4>
                  <p className="advisor-role">{advisor.role}</p>
                  <p className="advisor-company">{advisor.company}</p>
                  <p className="advisor-bio">{advisor.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="team-stats"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon">
                <Users size={32} />
              </div>
              <div className="stat-content">
                <span className="stat-number">15+</span>
                <span className="stat-label">Team Members</span>
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">
                <Award size={32} />
              </div>
              <div className="stat-content">
                <span className="stat-number">50+</span>
                <span className="stat-label">Years Combined Experience</span>
              </div>
            </div>
            
            <div className="stat-item">
              <div className="stat-icon">
                <Rocket size={32} />
              </div>
              <div className="stat-content">
                <span className="stat-number">3</span>
                <span className="stat-label">Successful Exits</span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="join-team"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          viewport={{ once: true }}
        >
          <div className="join-card">
            <h3>Join Our Mission</h3>
            <p>
              We're always looking for passionate individuals who want to make a difference 
              in the fitness industry. If you're excited about our vision, let's talk.
            </p>
            <motion.button
              className="btn btn-primary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              <Mail size={20} />
              Get in Touch
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Team;
